import React, { useState } from 'react';
import { usePermissions } from '../hooks/usePermissions';
import { Can } from '../components/Can';
import { AppRole } from '../types';
import { Shield, ShieldAlert, User as UserIcon, Settings, DollarSign, Users, Truck, LayoutDashboard } from 'lucide-react';

export function PermissionsDemo() {
  const { userRole, permissions, isLoading } = usePermissions();

  const handleLogin = (role: AppRole) => {
    localStorage.setItem('mock_user_role', role);
    window.location.reload(); // Reload to apply mock change since we simulate fetching in provider on mount
  };

  const handleLogout = () => {
    localStorage.removeItem('mock_user_role');
    window.location.reload();
  };

  if (isLoading) {
    return <div className="p-8">Carregando permissões...</div>;
  }

  return (
    <div className="p-8 max-w-6xl mx-auto space-y-8">
      <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
        <h1 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-2">
          <Shield className="w-6 h-6 text-indigo-600" />
          Sistema de Controle de Acesso
        </h1>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* User Session Info */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-slate-700">Sessão Atual</h2>
            {userRole !== 'guest' ? (
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-100 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-500">Função (Role)</span>
                  <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded-md text-xs font-bold uppercase">
                    {userRole}
                  </span>
                </div>
                <div className="pt-2 border-t border-slate-200">
                  <span className="text-sm font-medium text-slate-500 block mb-2">Permissões Ativas:</span>
                  <div className="flex flex-wrap gap-2">
                    {permissions.map((p) => (
                      <span key={p} className="px-2 py-1 bg-slate-200 text-slate-700 rounded-md text-xs">
                        {p}
                      </span>
                    ))}
                    {permissions.length === 0 && <span className="text-xs text-slate-400">Nenhuma</span>}
                  </div>
                </div>
                
                <button 
                  onClick={handleLogout}
                  className="w-full mt-4 px-4 py-2 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg text-sm font-medium transition-colors"
                >
                  Limpar Função (Visitante)
                </button>
              </div>
            ) : (
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-100 text-amber-800 flex items-center gap-3">
                <ShieldAlert className="w-5 h-5 text-amber-600" />
                <p className="text-sm font-medium">Sessão de visitante. Por favor, selecione uma função para simular o acesso.</p>
              </div>
            )}
          </div>

          {/* Role Switcher */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-slate-700">Assumir Função (Simulação)</h2>
            <div className="grid grid-cols-2 gap-3">
              {(['admin', 'manager', 'user', 'guest'] as AppRole[]).map((r) => (
                <button
                  key={r}
                  onClick={() => handleLogin(r)}
                  className={`px-4 py-3 rounded-lg border text-sm font-medium flex items-center justify-center gap-2 transition-all ${
                    userRole === r 
                      ? 'bg-indigo-600 text-white border-indigo-600 shadow-md shadow-indigo-200' 
                      : 'bg-white text-slate-700 border-slate-200 hover:border-indigo-300 hover:bg-indigo-50'
                  }`}
                >
                  <UserIcon className="w-4 h-4" />
                  {r === 'admin' ? 'Administrador' : r === 'manager' ? 'Gerente' : r === 'user' ? 'Usuário' : 'Visitante'}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Module Rendering based on Permissions */}
      {userRole !== 'guest' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          
          <Can permission="view_dashboard" fallback={
             <div className="p-6 bg-slate-50 border border-slate-200 rounded-xl flex flex-col items-center justify-center text-center text-slate-400 min-h-[200px]">
               <ShieldAlert className="w-8 h-8 mb-2 opacity-50" />
               <span className="text-sm">Acesso negado ao Dashboard</span>
             </div>
          }>
            <div className="p-6 bg-white border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-4 text-indigo-600">
                <LayoutDashboard className="w-6 h-6" />
                <h3 className="text-lg font-bold text-slate-800">Módulo Dashboard</h3>
              </div>
              <p className="text-sm text-slate-500 mb-4">Você tem acesso para visualizar métricas e resumos do dashboard.</p>
            </div>
          </Can>

          <Can permission="view_financial" fallback={
            <div className="p-6 bg-slate-50 border border-slate-200 rounded-xl flex flex-col items-center justify-center text-center text-slate-400 min-h-[200px]">
              <ShieldAlert className="w-8 h-8 mb-2 opacity-50" />
              <span className="text-sm">Acesso negado ao Financeiro</span>
            </div>
          }>
            <div className="p-6 bg-emerald-50 border border-emerald-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-4 text-emerald-600">
                <DollarSign className="w-6 h-6" />
                <h3 className="text-lg font-bold text-slate-800">Módulo Financeiro</h3>
              </div>
              <p className="text-sm text-slate-500 mb-4">Métricas financeiras, faturamento e notas fiscais estão disponíveis.</p>
              
              <Can permission="manage_financial">
                <button className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded shadow-sm w-full transition-colors">
                  Gerar Relatórios (Gerenciar)
                </button>
              </Can>
            </div>
          </Can>

          <Can permission="view_people" fallback={
            <div className="p-6 bg-slate-50 border border-slate-200 rounded-xl flex flex-col items-center justify-center text-center text-slate-400 min-h-[200px]">
              <ShieldAlert className="w-8 h-8 mb-2 opacity-50" />
              <span className="text-sm">Acesso negado a Pessoas (CRM)</span>
            </div>
          }>
            <div className="p-6 bg-blue-50 border border-blue-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-4 text-blue-600">
                <Users className="w-6 h-6" />
                <h3 className="text-lg font-bold text-slate-800">CRM de Pessoas</h3>
              </div>
              <p className="text-sm text-slate-500 mb-4">Visualize clientes, funcionários e fornecedores.</p>
              
              <Can permission="manage_people">
                <div className="flex gap-2">
                  <button className="flex-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded shadow-sm transition-colors">
                    Adicionar Pessoa
                  </button>
                  <button className="flex-1 px-3 py-1.5 bg-white border border-blue-300 text-blue-700 hover:bg-blue-100 text-xs font-bold rounded transition-colors">
                    Editar Perfil
                  </button>
                </div>
              </Can>
            </div>
          </Can>

          <Can permission="manage_users" fallback={
            <div className="p-6 bg-slate-50 border border-slate-200 rounded-xl flex flex-col items-center justify-center text-center text-slate-400 min-h-[200px]">
              <ShieldAlert className="w-8 h-8 mb-2 opacity-50" />
              <span className="text-sm">Acesso negado a Configurações de Usuários</span>
            </div>
          }>
            <div className="p-6 bg-purple-50 border border-purple-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3 mb-4 text-purple-600">
                <Settings className="w-6 h-6" />
                <h3 className="text-lg font-bold text-slate-800">Usuários do Sistema</h3>
              </div>
              <p className="text-sm text-slate-500 mb-4">Você tem privilégios de administrador para gerenciar contas do sistema.</p>
              <div className="space-y-2">
                <Can permission="create_user">
                  <button className="w-full px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold rounded shadow-sm transition-colors">
                    Criar Novo Usuário
                  </button>
                </Can>
                <Can permission="delete_user">
                  <button className="w-full px-3 py-1.5 bg-red-100 text-red-700 hover:bg-red-200 text-xs font-bold rounded transition-colors">
                    Excluir Contas
                  </button>
                </Can>
              </div>
            </div>
          </Can>
          
        </div>
      )}
    </div>
  );
}
